import React, { useState, useEffect } from 'react';
import { Layout, Menu, theme, Typography, Space, Avatar, Button, Card, Row, Col, Input, Select, Table, Pagination, Dropdown, message, Spin, Modal } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';
import UserProfile from './UserProfile';
import {
  DashboardOutlined, UserOutlined, StarOutlined, RiseOutlined, SettingOutlined,
  ExclamationCircleOutlined,
  MenuOutlined, SearchOutlined, UserAddOutlined,
  SortAscendingOutlined, EditOutlined, DeleteOutlined,
  GiftOutlined, TeamOutlined, GlobalOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CalculatorOutlined, MailOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import InviteVendorModal from './InviteVendorModal';
import VendorProfile from './VendorProfile';
import DashboardSection from './DashboardSection';
import VendorHighlights from './VendorHighlights';
import { vendorAPI, approvalsAPI, Vendor as VendorType } from '../services/api';
import { addNotification } from '../services/notifications';
import '../styles/sidebar-standard.css';
import '../styles/menu-hover-overrides.css';
import './Vendor.css';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const Vendor: React.FC = () => {
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState('30-days');
  const [inviteVendorModalVisible, setInviteVendorModalVisible] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [profileVisible, setProfileVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vendorsData, setVendorsData] = useState<any[]>([]);
  const [allVendorsData, setAllVendorsData] = useState<any[]>([]);
  const [highlights, setHighlights] = useState<any>(null);
  const [totalVendors, setTotalVendors] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
  const [selectedLocation, setSelectedLocation] = useState<string | undefined>();
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // Load vendors from API
  useEffect(() => {
    loadVendors();
  }, [pageSize]);

  const loadVendors = async () => {
    setLoading(true);
    setError(null);

    // No mock data fallback - use real API data only

    // Fire highlights fetch in parallel — independent of the table data.
    vendorAPI
      .getVendorHighlights()
      .then((r: any) =>
        setHighlights(r?.success ? r.data : null),
      )
      .catch(() => setHighlights(null));

    try {
      const collected: any[] = [];
      let page = 1;
      const limit = Math.max(pageSize, 200);
      let total = 0;
      let response: any = null;

      do {
        response = await vendorAPI.getVendors(page, limit);
        if (response?.success && Array.isArray(response.data)) {
          collected.push(...response.data);
        }
        total = response?.pagination?.total || collected.length;
        page += 1;
      } while (collected.length < total);

      // Ensure data is an array before processing - handle all cases
      let vendorsData: VendorType[] = [];
      
      if (collected.length > 0) {
        vendorsData = collected;
      } else {
        vendorsData = [];
      }
      
      
      if (response?.success && vendorsData.length > 0) {
        // Transform API data to match our table structure
          const transformedData = vendorsData.map((vendor: VendorType) => {
            const rawStatus = ((vendor as any).status ?? ((vendor as any).is_active !== false ? 'active' : 'inactive')).toString().toLowerCase();
            const normalizedStatus = rawStatus === 'active' ? 'active' : 'inactive';
            const isActive = normalizedStatus === 'active';
            const signupStatus = ((vendor as any).signup_status || 'approved').toString().toLowerCase();
            const isEnabled =
              (vendor as any).is_enabled !== undefined
                ? Boolean((vendor as any).is_enabled)
                : (vendor as any).enabled !== undefined
                ? Boolean((vendor as any).enabled)
                : isActive;

            return ({
            key: vendor.id.toString(),
            name: vendor.name,
            // Prefer the vendor owner's account name (from users table for
            // self-signup vendors) over a manually-entered contact_name.
            contactName:
              (vendor as any).account_owner_name ||
              (vendor as any).contact_name ||
              (vendor as any).contactName ||
              vendor.name ||
              vendor.email,
            // Show the signup/login email for self-signup vendors; fall back
            // to vendors.email (public contact) for legacy admin-created rows.
            email: (vendor as any).account_email || vendor.email,
            contact: vendor.phone,
            category: vendor.category || 'Uncategorized',
            cityState: vendor.address && vendor.address.city && vendor.address.state 
              ? `${vendor.address.city}, ${vendor.address.state}`
              : vendor.address && vendor.address.city 
              ? vendor.address.city
              : 'Location not specified',
            tier: '$$', // Default tier, could be calculated based on data
            discount: 10, // Default discount, should come from discounts API
            active: (vendor as any).is_active !== undefined
              ? Boolean((vendor as any).is_active)
              : (vendor as any).active !== undefined
              ? Boolean((vendor as any).active)
              : isActive,
            enabled: isEnabled,
            status: normalizedStatus, // Use normalized vendor status
            signupStatus, // 'pending' / 'approved' / 'rejected' from the new workflow
            avatar: vendor.name.charAt(0).toUpperCase(),
            logo_url: vendor.logo_url || null // Include logo URL for display
          });
        });
        setVendorsData(transformedData);
        setAllVendorsData(transformedData);
        setTotalVendors(transformedData.length);
      } else if (response?.success && vendorsData.length === 0) {
        // Success but no vendors yet
        setVendorsData([]);
        setAllVendorsData([]);
        setTotalVendors(0);
      } else {
        console.error('❌ Failed to load vendors:', response);
        setError((response as any).error || response.message || 'Failed to load vendors');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error loading vendors:', error);
      console.error('Error details:', error);
      setVendorsData([]);
      setAllVendorsData([]);
      setTotalVendors(0);
      setError('Failed to load vendors');
      setLoading(false);
    }
  };

  const handleToggleChange = async (key: string, field: 'active' | 'enabled') => {
    try {
      const vendorId = parseInt(key, 10);
      const currentVendor = allVendorsData.find(v => v.key === key);
      if (!currentVendor) return;

      let response;
      if (field === 'active') {
        const nextStatus = currentVendor.status === 'active' ? 'inactive' : 'active';
        const action = nextStatus === 'active' ? 'activate' : 'deactivate';
        response = await vendorAPI.updateVendorStatus(vendorId, nextStatus);
        if (response.success) {
          setAllVendorsData(prevData =>
            prevData.map(item =>
              item.key === key
                ? { ...item, status: nextStatus, active: nextStatus === 'active', enabled: nextStatus === 'active' }
                : item
            )
          );
          setVendorsData(prevData =>
            prevData.map(item =>
              item.key === key
                ? { ...item, status: nextStatus, active: nextStatus === 'active', enabled: nextStatus === 'active' }
                : item
            )
          );
          message.success(`Vendor ${action}d successfully`);
          loadVendors(); // Refresh from server
        } else {
          message.error(`Failed to ${action} vendor: ${response.error || 'Unknown error'}`);
        }
      } else {
        const nextEnabled = !currentVendor.enabled;
        response = await vendorAPI.updateVendor(vendorId, { is_enabled: nextEnabled });
        if (response.success) {
          setAllVendorsData(prevData =>
            prevData.map(item =>
              item.key === key ? { ...item, enabled: nextEnabled } : item
            )
          );
          setVendorsData(prevData =>
            prevData.map(item =>
              item.key === key ? { ...item, enabled: nextEnabled } : item
            )
          );
          message.success(`Vendor ${nextEnabled ? 'enabled' : 'disabled'} successfully`);
          loadVendors(); // Refresh from server
        } else {
          message.error(`Failed to update vendor: ${response.error || 'Unknown error'}`);
        }
      }
      if (!response.success) {
        addNotification({
          title: 'Vendor update failed',
          message: response.error || `Failed to update vendor ${field}.`,
          level: 'error',
        });
      }
    } catch (error) {
      console.error(`Error updating vendor ${field}:`, error);
      message.error(`Failed to update vendor ${field} status`);
      addNotification({
        title: 'Vendor update failed',
        message: `Failed to update vendor ${field} status.`,
        level: 'error',
      });
    }
  };

  const handleTimeFilterChange = (key: string) => {
    setSelectedTimeFilter(key);
  };

  const handleInviteVendor = () => {
    setInviteVendorModalVisible(true);
  };

  const handleInviteVendorModalCancel = () => {
    setInviteVendorModalVisible(false);
  };

  const handleInviteVendorModalSubmit = async (values: any) => {
    // The modal already handles vendor creation, we just need to refresh the list
    setInviteVendorModalVisible(false);
    // Refresh the vendor list with a small delay to ensure backend processing is complete
    setTimeout(() => {
      loadVendors();
    }, 500);
  };

  const handleVendorClick = (vendorId: string) => {
    setSelectedVendorId(vendorId);
    setProfileVisible(true);
  };

  const handleProfileClose = () => {
    setProfileVisible(false);
    setSelectedVendorId(null);
  };

  const handleVendorUpdate = async (updatedData: any) => {
    
    // If updateData indicates success, just refresh the list (update was already done in VendorProfile)
    if (updatedData?.success === true) {
      loadVendors();
      return;
    }
    
    // Otherwise, this is a legacy call - try to update (but this shouldn't happen)
    try {
      if (selectedVendorId && updatedData && typeof updatedData === 'object' && !updatedData.success) {
        const vendorId = parseInt(selectedVendorId);
        const result = await vendorAPI.updateVendor(vendorId, updatedData);
        if (result.success || result.data) {
          message.success('Vendor updated successfully!');
          loadVendors();
        } else {
          const errorMsg = result.error || 'Failed to update vendor. Please try again.';
          console.error('🔄 Vendor.tsx: Update failed:', errorMsg);
          message.error(errorMsg);
        }
      } else {
        // Just refresh the list
        loadVendors();
      }
    } catch (error: any) {
      console.error('🔄 Vendor.tsx: Error updating vendor:', error);
      console.error('🔄 Vendor.tsx: Error details:', error.message, error.stack);
      // Don't show error if update was already successful
      if (!updatedData?.success) {
        message.error(error.message || 'Failed to update vendor. Please try again.');
      }
    }
  };

  const handleEditVendor = (record: any) => {
    // Open the vendor profile in edit mode
    setSelectedVendorId(record.key);
    setProfileVisible(true);
  };

  const handleDeleteVendor = (record: any) => {
    // Show confirmation modal
    Modal.confirm({
      title: 'Are you sure you want to delete this vendor?',
      content: `This will permanently delete "${record.name}" and all associated data. This action cannot be undone.`,
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          
          const response = await vendorAPI.deleteVendor(parseInt(record.key));
            const isSuccess = response?.error !== undefined ? false : (response?.success !== false || response?.data !== undefined);
            if (isSuccess) {
              message.success('Vendor deleted successfully');
              await loadVendors();
            } else {
              message.error(response?.error || response?.message || 'Failed to delete vendor');
              throw new Error('Delete failed');
            }
        } catch (error) {
          console.error('Error deleting vendor:', error);
          message.error('Failed to delete vendor. Please try again.');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  // Quick-approve directly from the vendors list — same backend as the Pending
  // Approvals page. Shown only for rows whose signupStatus === 'pending'.
  const handleApproveVendor = (record: any) => {
    Modal.confirm({
      title: `Approve ${record.name}?`,
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      content:
        'The vendor will go live on the donor app immediately and receive an approval email.',
      okText: 'Approve',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await approvalsAPI.approveItem(parseInt(record.key), 'vendor');
          if (response.success) {
            message.success(`${record.name} approved`);
            addNotification({
              title: 'Vendor approved',
              message: record.name,
              level: 'success',
            });
            loadVendors();
          } else {
            message.error('Failed to approve vendor');
          }
        } catch (err) {
          console.error('Error approving vendor:', err);
          message.error('Failed to approve vendor. Please try again.');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleRejectVendor = (record: any) => {
    let reason = '';
    Modal.confirm({
      title: `Reject ${record.name}?`,
      icon: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            The vendor will be marked rejected and receive an email with the reason below.
          </p>
          <Input.TextArea
            rows={3}
            placeholder="Optional — reason shown to the vendor in the rejection email"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: 'Reject',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await approvalsAPI.rejectItem(
            parseInt(record.key),
            'vendor',
            reason.trim() || 'Rejected by admin',
          );
          if (response.success) {
            message.success(`${record.name} rejected`);
            addNotification({
              title: 'Vendor rejected',
              message: record.name,
              level: 'warning',
            });
            loadVendors();
          } else {
            message.error('Failed to reject vendor');
          }
        } catch (err) {
          console.error('Error rejecting vendor:', err);
          message.error('Failed to reject vendor. Please try again.');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const timeFilterMenu = [
    {
      key: '7-days',
      label: 'Last 7 Days',
      onClick: () => handleTimeFilterChange('7-days')
    },
    {
      key: '30-days',
      label: 'Last 30 Days',
      onClick: () => handleTimeFilterChange('30-days')
    },
    {
      key: '90-days',
      label: 'Last 90 Days',
      onClick: () => handleTimeFilterChange('90-days')
    },
    {
      key: '1-year',
      label: 'Last 1 Year',
      onClick: () => handleTimeFilterChange('1-year')
    },
    {
      key: 'all-time',
      label: 'All Time',
      onClick: () => handleTimeFilterChange('all-time')
    }
  ];

  const columns = [
    {
      title: (
        <div className="sortable-header">
          Vendor name
          <SortAscendingOutlined className="sort-icon" />
        </div>
      ),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortField === 'name' ? sortOrder : null,
      render: (text: string, record: any) => (
        <Space>
          <Avatar 
            size={32} 
            src={record.logo_url} // Use logo URL if available
            style={{ backgroundColor: record.logo_url ? 'transparent' : '#DB8633' }}
            icon={!record.logo_url ? record.avatar : undefined} // Show initial if no logo
          >
            {!record.logo_url && record.avatar}
          </Avatar>
          <Text 
            strong 
            className="clickable-vendor-name"
            onClick={() => handleVendorClick(record.key)}
            style={{ cursor: 'pointer', color: '#DB8633' }}
          >
            {text}
          </Text>
        </Space>
      ),
      fixed: 'left' as const,
      width: 200,
    },
    { 
      title: 'Contact name', 
      dataIndex: 'contactName', 
      key: 'contactName', 
      render: (text: string) => <Text type="secondary">{text}</Text>, 
      width: 150 
    },
    { 
      title: 'Emails', 
      dataIndex: 'email', 
      key: 'email', 
      render: (text: string) => <Text type="secondary">{text}</Text>, 
      width: 200 
    },
    { 
      title: 'Contact number', 
      dataIndex: 'contact', 
      key: 'contact', 
      render: (text: string) => <Text type="secondary">{text}</Text>, 
      width: 150 
    },
    { 
      title: 'Category', 
      dataIndex: 'category', 
      key: 'category', 
      render: (text: string) => <Text type="secondary">{text}</Text>, 
      width: 200 
    },
    { 
      title: 'City, State', 
      dataIndex: 'cityState', 
      key: 'cityState', 
      render: (text: string) => <Text type="secondary">{text}</Text>, 
      width: 150 
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      render: (text: string) => <Text strong style={{ color: '#DB8633' }}>{text}</Text>,
      width: 80,
    },
    {
      title: 'Dis',
      dataIndex: 'discount',
      key: 'discount',
      render: (text: number) => <Text strong style={{ color: '#DB8633' }}>{text}</Text>,
      width: 80,
    },
    {
      title: 'Active/De-active',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean, record: any) => (
        <div className="toggle-switch">
          <div className={`toggle ${active ? 'active' : 'inactive'}`} onClick={() => handleToggleChange(record.key, 'active')}>
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 140,
    },
    {
      title: 'Enable/Disable',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled: boolean, record: any) => (
        <div className="enable-disable-toggle">
          <div className={`toggle ${enabled ? 'active' : 'inactive'}`} onClick={() => handleToggleChange(record.key, 'enabled')}>
            <div className="toggle-slider"></div>
          </div>
        </div>
      ),
      width: 140,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: any) => {
        // signup_status from the vendor portal workflow takes precedence over
        // the legacy active/inactive flag — pending and rejected vendors are
        // pre-approval so "Active" wouldn't make sense.
        let label = status === 'active' ? 'Active' : 'Inactive';
        let bg = '#fff2e8', fg = '#fa8c16', border = '#ffd591'; // orange (inactive default)
        if (status === 'active') {
          bg = '#f6ffed'; fg = '#52c41a'; border = '#b7eb8f'; // green
        }
        if (record.signupStatus === 'pending') {
          label = 'Pending'; bg = '#fffbe6'; fg = '#d48806'; border = '#ffe58f'; // amber
        } else if (record.signupStatus === 'rejected') {
          label = 'Rejected'; bg = '#fff1f0'; fg = '#cf1322'; border = '#ffa39e'; // red
        }
        return (
          <Space size="small">
            <span
              className="status-badge"
              style={{
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                backgroundColor: bg,
                color: fg,
                border: `1px solid ${border}`,
              }}
            >
              {label}
            </span>
          </Space>
        );
      },
      width: 110,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text: string, record: any) => (
        <Space size="small">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small" 
              className="edit-action-btn"
              onClick={(e) => {
                e.stopPropagation();
                handleEditVendor(record);
              }}
              title="Edit Vendor"
            />
            {record.signupStatus === 'pending' ? (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  size="small"
                  className="status-toggle-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApproveVendor(record);
                  }}
                  title="Approve Vendor"
                >
                  Approve
                </Button>
                <Button
                  danger
                  icon={<CloseCircleOutlined />}
                  size="small"
                  className="status-toggle-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRejectVendor(record);
                  }}
                  title="Deny Vendor"
                >
                  Deny
                </Button>
              </>
            ) : record.signupStatus === 'rejected' ? (
              <span
                className="signup-status-label denied"
                title="Vendor was denied via the Pending Approvals workflow"
              >
                Denied
              </span>
            ) : (
              <span
                className="signup-status-label approved"
                title="Vendor is approved and visible in the donor app"
              >
                Approved
              </span>
            )}
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteVendor(record);
              }}
              title="Delete Vendor"
            />
        </Space>
      ),
      width: 200,
      fixed: 'right' as const,
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [searchTerm, selectedCategory, selectedStatus, selectedLocation, showInactive]);

  const uniqueCategories = Array.from(new Set(allVendorsData.map(vendor => vendor.category).filter(Boolean)));
  const uniqueStatuses = Array.from(new Set(allVendorsData.map(vendor => vendor.status).filter(Boolean)));
  const uniqueLocations = Array.from(new Set(allVendorsData.map(vendor => vendor.cityState).filter(Boolean)));

  const filteredVendors = allVendorsData.filter((vendor) => {
    // Default view: active vendors + anything still awaiting approval so the
    // admin can approve/reject them right from the list. Explicit status
    // filter or "Show Inactive" toggle override this.
    const matchesStatus = selectedStatus
      ? vendor.status === selectedStatus
      : showInactive
      ? true
      : vendor.status === 'active' || (vendor as any).signupStatus === 'pending';
    const matchesCategory = selectedCategory ? vendor.category === selectedCategory : true;
    const matchesLocation = selectedLocation ? vendor.cityState === selectedLocation : true;
    const matchesSearch = searchTerm
      ? [vendor.name, vendor.contactName, vendor.email, vendor.contact, vendor.category, vendor.cityState]
          .filter(Boolean)
          .some((value: string) => value.toLowerCase().includes(searchTerm.toLowerCase()))
      : true;

    return matchesStatus && matchesCategory && matchesLocation && matchesSearch;
  });

  const sortedVendors = [...filteredVendors].sort((a, b) => {
    if (!sortField || !sortOrder) return 0;
    const valueA = (a as any)[sortField];
    const valueB = (b as any)[sortField];
    const normalizedA = valueA ? valueA.toString().toLowerCase() : '';
    const normalizedB = valueB ? valueB.toString().toLowerCase() : '';
    const comparison = normalizedA.localeCompare(normalizedB);
    return sortOrder === 'ascend' ? comparison : -comparison;
  });

  const paginatedVendors = sortedVendors.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <Layout className="vendor-layout">
      <AdminSidebar
        activeKey="vendor"
        mobileVisible={mobileSidebarVisible}
        onMobileToggle={() => setMobileSidebarVisible(!mobileSidebarVisible)}
      />

      {/* Main Content */}
      <Layout className="standard-main-content">
        <Header className="vendor-header">
          <div className="header-left">
            <Title level={2} style={{ margin: 0 }}>Vendors</Title>
            <Text type="secondary" className="vendors-count">
              {filteredVendors.length} Vendor{filteredVendors.length !== 1 ? 's' : ''} Found
              {filteredVendors.length !== totalVendors && ` (of ${totalVendors} total)`}
            </Text>
          </div>
          <div className="header-right">
            <Button 
              type="primary" 
              icon={<UserAddOutlined />}
              size="large"
              className="invite-vendor-btn"
              onClick={handleInviteVendor}
            >
              Invite A Vendor
            </Button>
          </div>
        </Header>

        <Content className="vendor-content">
          <div className="content-wrapper">
            <DashboardSection
              title="Vendor Highlights"
              subtitle="Partner activity and engagement at a glance"
              icon={<TrophyOutlined />}
            >
              <VendorHighlights data={highlights} />
            </DashboardSection>

            <DashboardSection
              title="All Vendors"
              subtitle="Search, filter, and manage individual vendor partners"
              icon={<RiseOutlined />}
            >
            {/* Search and Filter Bar */}
            <div className="search-filter-bar">
              <div className="search-section">
                <Search
                  placeholder="Search Vendors"
                  allowClear
                  enterButton={<SearchOutlined />}
                  size="large"
                  className="vendor-search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>
              
              <div className="filter-section">
                <Text strong className="filter-label">Filters</Text>
                <div className="filter-dropdowns">
                  <div className="filter-item">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input 
                        type="checkbox" 
                        checked={showInactive}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        style={{ margin: 0 }}
                      />
                      <Text>Show Inactive Vendors</Text>
                    </label>
                  </div>
                  <Select
                    placeholder="Select Category"
                    className="filter-dropdown"
                    size="large"
                    value={selectedCategory}
                    onChange={(value) => setSelectedCategory(value)}
                    allowClear
                  >
                    {uniqueCategories.map((category) => (
                      <Option key={category} value={category}>
                        {category}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    placeholder="Select Status"
                    className="filter-dropdown"
                    size="large"
                    value={selectedStatus}
                    onChange={(value) => setSelectedStatus(value)}
                    allowClear
                  >
                    {uniqueStatuses.map((status) => (
                      <Option key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    placeholder="Select Location"
                    className="filter-dropdown"
                    size="large"
                    value={selectedLocation}
                    onChange={(value) => setSelectedLocation(value)}
                    allowClear
                  >
                    {uniqueLocations.map((location) => (
                      <Option key={location} value={location}>
                        {location}
                      </Option>
                    ))}
                  </Select>
                  
                  <Select
                    placeholder="Rating"
                    className="filter-dropdown"
                    size="large"
                    disabled
                  />

                  <Button
                    size="large"
                    disabled={!searchTerm && !selectedCategory && !selectedStatus && !selectedLocation}
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory(undefined);
                      setSelectedStatus(undefined);
                      setSelectedLocation(undefined);
                    }}
                    style={{ color: '#DB8633', borderColor: '#DB8633' }}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
              

            </div>

            {/* Vendors Table */}
            <div className="vendors-table-section">
              <Spin spinning={loading}>
              <Table
                dataSource={paginatedVendors}
                columns={columns}
                pagination={false}
                size="middle"
                className="vendors-table"
                rowClassName="vendor-row"
                scroll={{ x: 1800 }}
                bordered={false}
                onChange={(_, __, sorter) => {
                  if (Array.isArray(sorter)) {
                    return;
                  }
                  const nextField = (sorter as any).field as string | undefined;
                  const nextOrder = (sorter as any).order as 'ascend' | 'descend' | null | undefined;
                  setSortField(nextField || null);
                  setSortOrder(nextOrder || null);
                }}
                  locale={{
                    emptyText: error ? `Error: ${error}` : 'No vendors found'
                  }}
              />
              </Spin>
              
              {/* Pagination */}
              <div className="pagination-section">
                <Pagination
                  current={currentPage}
                  total={filteredVendors.length}
                  pageSize={pageSize}
                  showSizeChanger={false}
                  showQuickJumper={false}
                  onChange={handlePageChange}
                  className="vendors-pagination"
                />
              </div>
            </div>
            </DashboardSection>
          </div>
        </Content>
      </Layout>

      {/* Invite Vendor Modal */}
      <InviteVendorModal
        visible={inviteVendorModalVisible}
        onCancel={handleInviteVendorModalCancel}
        onSubmit={handleInviteVendorModalSubmit}
      />

      {/* Vendor Profile Modal */}
      {profileVisible && selectedVendorId && (
        <VendorProfile
          vendorId={selectedVendorId}
          onClose={handleProfileClose}
          onUpdate={handleVendorUpdate}
        />
      )}
    </Layout>
  );
};

export default Vendor; 